import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableBody, TableHead, TableRow, TableCell,
  Typography, Box, Alert, CircularProgress, Chip, Divider,
  TextField, LinearProgress,
} from '@mui/material';
import { errorToastMessage } from '../../../utils/toast';

interface RedactionPreviewHeader {
  participantId: string;
  participantName: string;
  participantEmail: string;
  studyName?: string;
}

interface RedactionPreviewRow {
  slNo: number;
  fieldName: string;
  fieldValue: string;
  action: 'REDACTED' | 'TRUNCATED' | 'BLURRED';
  revivalPossible: string;
  table: string;
}

interface RedactionPreviewResponse {
  header: RedactionPreviewHeader;
  rows: RedactionPreviewRow[];
  totalRows: number;
  reversibleCount: number;
  permanentCount: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  fetchPreview: () => Promise<RedactionPreviewResponse>;
  /**
   * Flips wording, action column, and CTA semantics. The BE returns the same
   * `RedactionPreviewResponseDto` shape for both flows; only the labels change.
   * Defaults to `'redact'` so existing call sites are unaffected.
   */
  mode?: 'redact' | 'revive';
}

type ModalState = 'loading' | 'loaded' | 'confirming' | 'error';

const RedactionPreviewModal: React.FC<Props> = ({ open, onClose, onConfirm, fetchPreview, mode = 'redact' }) => {
  const isRevive = mode === 'revive';
  const [state, setState] = useState<ModalState>('loading');
  const [preview, setPreview] = useState<RedactionPreviewResponse | null>(null);
  const [reason, setReason] = useState('');

  const loadPreview = async () => {
    setState('loading');
    try {
      const data = await fetchPreview();
      if (!data) {
        throw new Error('Preview response was empty — check server logs.');
      }
      setPreview(data);
      setState('loaded');
    } catch (err) {
      setState('error');
      errorToastMessage(err as Error);
    }
  };

  useEffect(() => {
    if (open) {
      setReason('');
      loadPreview();
    }
  }, [open]);

  const handleConfirm = async () => {
    setState('confirming');
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      setState('loaded'); // revert — preview still valid
      errorToastMessage(err as Error);
    }
  };

  const handleClose = () => {
    if (state === 'confirming') return; // block close during confirm
    onClose();
  };

  const reversibleRows = preview?.rows.filter(r => r.revivalPossible.startsWith('Yes')) ?? [];
  const permanentRows = preview?.rows.filter(r => r.revivalPossible.startsWith('No')) ?? [];
  const isConfirming = state === 'confirming';
  const isConfirmDisabled = state !== 'loaded' || reason.trim().length === 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle>{isRevive ? 'Revive Preview' : 'Redaction Preview'}</DialogTitle>
      <DialogContent>
        {/* Header info */}
        {preview?.header && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
            {preview.header.studyName && (
              <Typography variant="body2"><strong>Study:</strong> {preview.header.studyName}</Typography>
            )}
            <Typography variant="body2"><strong>Participant ID:</strong> {preview.header.participantId}</Typography>
            <Typography variant="body2"><strong>Name:</strong> {preview.header.participantName}</Typography>
            <Typography variant="body2"><strong>Email:</strong> {preview.header.participantEmail}</Typography>
          </Box>
        )}

        <Alert severity={isRevive ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {isRevive
            ? 'This will restore the redacted fields from escrow. Review the values that will be written back before confirming.'
            : 'This action cannot be fully undone. Review all fields carefully before confirming.'}
        </Alert>

        {state === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {state === 'error' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load preview. Please try again.
            </Alert>
            <Button variant="outlined" onClick={loadPreview}>Retry</Button>
          </Box>
        )}

        {(state === 'loaded' || state === 'confirming') && preview && (
          <>
            {isConfirming && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {isRevive
                    ? 'Reviving participant data — this may take a few seconds.'
                    : 'Redacting participant data — this may take a few seconds.'}
                </Typography>
              </Box>
            )}
            <TextField
              label={isRevive ? 'Reason for revival' : 'Reason for redaction'}
              required
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isConfirming}
              error={reason.trim().length === 0}
              helperText={
                reason.trim().length === 0
                  ? 'Required — recorded in the audit trail.'
                  : `${reason.length}/1000`
              }
              inputProps={{ maxLength: 1000 }}
              sx={{ mb: 2 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {isRevive
                ? `${preview.totalRows} field${preview.totalRows === 1 ? '' : 's'} will be restored`
                : `${preview.reversibleCount} reversible · ${preview.permanentCount} permanent`}
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Field Name</TableCell>
                    <TableCell>{isRevive ? 'Value To Restore' : 'Current Value'}</TableCell>
                    <TableCell>Action</TableCell>
                    {!isRevive && <TableCell>Revival Possible</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isRevive ? (
                    preview.rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          No fields are stored in escrow for this study redaction.
                        </TableCell>
                      </TableRow>
                    ) : (
                      preview.rows.map(row => (
                        <TableRow key={row.slNo}>
                          <TableCell>{row.slNo}</TableCell>
                          <TableCell>{row.fieldName}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.fieldValue}</TableCell>
                          <TableCell>
                            <Chip label="RESTORE" size="small" color="success" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))
                    )
                  ) : (
                    <>
                      {reversibleRows.map(row => (
                        <TableRow key={row.slNo}>
                          <TableCell>{row.slNo}</TableCell>
                          <TableCell>{row.fieldName}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.fieldValue}</TableCell>
                          <TableCell>{row.action}</TableCell>
                          <TableCell>
                            <Chip label={row.revivalPossible} size="small" color="success" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                      {reversibleRows.length > 0 && permanentRows.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ p: 0 }}>
                            <Divider />
                          </TableCell>
                        </TableRow>
                      )}
                      {permanentRows.map(row => (
                        <TableRow key={row.slNo}>
                          <TableCell>{row.slNo}</TableCell>
                          <TableCell>{row.fieldName}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.fieldValue}</TableCell>
                          <TableCell>{row.action}</TableCell>
                          <TableCell>
                            <Chip label={row.revivalPossible} size="small" color="error" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isConfirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isRevive ? 'primary' : 'error'}
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          startIcon={
            isConfirming ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isConfirming
            ? (isRevive ? 'Reviving…' : 'Redacting…')
            : (isRevive ? 'Confirm Revival' : 'Confirm Redaction')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RedactionPreviewModal;
